import { ReactNode } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RoutePaths } from '../../../../config/route-paths';
import { DateTime } from '../../../../lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from '../../../../lib/utils/date-handling/DateTime.types';
import { selectIsLoggedIn } from '../../../cross-cutting-concerns/authentication/state/authenticationSelectors';
import { selectHasAgreedToCurrentTerms } from '../../../modules/terms-conditions/state/termsConditionsSelectors';
import { LegalInfoUtils } from '../../../utils/legal-info/LegalInfoUtils';
import { StyledPublicFooter } from './PublicFooter.styles';

interface PublicFooterProps {
  children?: ReactNode;
  className?: string;
}

export const PublicFooter = ({ children, className }: PublicFooterProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const hasAgreedToCurrentTerms = useSelector(selectHasAgreedToCurrentTerms);
  const footerClassName = classnames(className, 'footer');
  const shouldShowTermsAndConditionsLink = isLoggedIn && hasAgreedToCurrentTerms;

  return (
    <StyledPublicFooter className={footerClassName}>
      {children}
      <div className="footer__banner">
        <span className="footer__banner-title">{t('footer.service')}</span>
        <span className="footer__banner-subtitle">{t('footer.terms')}</span>
      </div>
      <div className="footer__bottom">
        <div className="footer__legal-links">
          <div className="footer__legal-link">
            <a href={LegalInfoUtils.getImprintUrl(i18n.language)} target="_blank" rel="noreferrer noopener">
              {t('footer.links.legal.imprint')}
            </a>
          </div>
          <div className="footer__legal-link">
            <Link to={RoutePaths.PRIVACY_POLICY} target="_blank" rel="noreferrer noopener">
              {t('footer.links.legal.privacyPolicy')}
            </Link>
          </div>
          {!shouldShowTermsAndConditionsLink && (
            <div className="footer__legal-link footer__legal-link--last">
              <Link to={RoutePaths.TERMS_CONDITIONS}>{t('common.termsAndConditions')}</Link>
            </div>
          )}
        </div>
        <div className="footer__copyright">
          {`© ${DateTime.formatDateByLocale(i18n.language, new Date().toISOString(), DATE_FORMAT_PATTERN.YEAR)} ${t(
            'footer.copyright'
          )}`}
        </div>
      </div>
    </StyledPublicFooter>
  );
};
